import { StructuredInput, StructuredInputToggle } from '../../../../../types';

export const EMPTY_TOGGLE_MESSAGE = 'Add at least one field to every unit toggle';
export const MISSING_FIELD_ID_MESSAGE = 'Give every field an id';
export const DUPLICATE_FIELD_ID_MESSAGE = 'Give each field in a toggle its own id';
export const UNRESOLVED_TEMPLATE_MESSAGE = 'An answer template names a field the toggle does not have';

/**
 * A toggle with nothing to type into renders a widget the patient cannot answer, and the
 * api rejects it outright. Nothing configured at all is fine — the question simply stays
 * a plain text box.
 */
export const validateStructuredInput = (input?: StructuredInput | null) => {
  if (!input?.toggles?.length) return true;

  const everyToggleHasAField = input.toggles.every(toggle => Boolean(toggle.fields?.length));
  if (!everyToggleHasAField) return EMPTY_TOGGLE_MESSAGE;

  const everyFieldHasAnId = input.toggles.every(hasIdOnEveryField);
  if (!everyFieldHasAnId) return MISSING_FIELD_ID_MESSAGE;

  const everyToggleHasDistinctIds = input.toggles.every(hasDistinctFieldIds);
  if (!everyToggleHasDistinctIds) return DUPLICATE_FIELD_ID_MESSAGE;

  const everyTemplateResolves = input.toggles.every(hasResolvableTemplate);
  if (!everyTemplateResolves) return UNRESOLVED_TEMPLATE_MESSAGE;

  return true;
};

// A new field starts without one, and the api stores a field by its id, so an id left
// blank fails the save rather than being quietly dropped.
const hasIdOnEveryField = (toggle: StructuredInputToggle) =>
  (toggle.fields ?? []).every(field => Boolean(field.id?.trim()));

// Fields are addressed by id within their own toggle, so two the same leave a template
// placeholder with no single field to fill it.
const hasDistinctFieldIds = (toggle: StructuredInputToggle) => {
  const ids = (toggle.fields ?? []).map(field => field.id.trim());

  return new Set(ids).size === ids.length;
};

// A template is composed by substituting {field_id}, so a placeholder naming a field that
// has since been removed would reach the patient verbatim.
const hasResolvableTemplate = (toggle: StructuredInputToggle) => {
  if (!toggle.answer_template) return true;

  const available = (toggle.fields ?? []).map(field => field.id.trim());
  // matchAll would need a polyfill at this browser target, so the plain match is used.
  const referenced = (toggle.answer_template.match(/{[^{}]*}/g) ?? []).map(placeholder =>
    placeholder.slice(1, -1).trim()
  );

  return referenced.every(id => available.indexOf(id) !== -1);
};
